import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { User } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { userFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import { userSortFn } from "../../sorting/utils"
import { UserMultiSelectGroup, type UserMultiSelectGroupProps } from "../MenuMultiSelectGroups/UserMultiSelectGroup"

interface UserMultiSelectProps
	extends
		UserMultiSelectGroupProps,
		MenuVariantProps,
		MenuRootProps,
		Omit<MenuClickTriggerProps, "children" | "ref"> {
	children: ReactElement
}

export const UserMultiSelect = memo((props: UserMultiSelectProps) => {
	const {
		children,
		disabled,
		users,
		defaultValues,
		values,
		onValuesChange,
		size,
		variant,
		accentColor,
		radius,
		...rest
	} = props

	const sortedUsers = useSort(users, userSortFn)

	const [filteredUsers, filterValue, setFilterValue] = useTextFilter<User>(sortedUsers, userFilterCallback)

	const isEmpty = filteredUsers.length === 0

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} variant={variant} accentColor={accentColor} radius={radius}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{!isEmpty ? <Menu.Separator /> : null}
				<Menu.Scroll>
					{!isEmpty ? (
						<UserMultiSelectGroup
							users={filteredUsers}
							defaultValues={defaultValues}
							values={values}
							onValuesChange={onValuesChange}
						/>
					) : null}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
UserMultiSelect.displayName = "UserMultiSelect"
